import { css, cx } from "@emotion/css";
import { Badge, Tooltip } from "@octopusdeploy/design-system-components";
import { TenantsIcon } from "@octopusdeploy/design-system-icons";
import { space, borderRadius, themeTokens, text } from "@octopusdeploy/design-system-tokens";
import type { DetailedDashboardItemResource, ProjectsPageDeployment, TenantDeploymentProgress, EnvironmentLiveStatusResource } from "@octopusdeploy/octopus-server-client";
import { TaskState } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { LiveStatusDescription } from "~/areas/projects/components/Observability/LiveStatusDescription";
import { LiveStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcon";
import TaskStatusIcon from "~/areas/projects/components/TaskStatusIcon";
import { ChannelChip } from "~/components/Chips";
import InternalLink from "~/components/Navigation/InternalLink";
import DateFormatter from "~/utils/DateFormatter";
interface DeploymentTaskStatusProps {
    projectSlug: string;
    deployment: DetailedDashboardItemResource | ProjectsPageDeployment;
    isCollapsed: boolean;
    tenantProgress: TenantDeploymentProgress | "tenanted" | "hidden";
    spaceId: string;
    channelName?: string;
    liveStatus: EnvironmentLiveStatusResource | undefined;
    forceLiveStatusIcon?: boolean;
}
export const DeploymentTaskStatus = ({ projectSlug, deployment, isCollapsed, tenantProgress, spaceId, channelName, liveStatus, forceLiveStatusIcon }: DeploymentTaskStatusProps) => {
    const containerRef = React.useRef<HTMLAnchorElement>(null);
    const relevantTime = deployment.CompletedTime ?? deployment.QueueTime;
    let taskStatusIcon = <TaskStatusIcon item={deployment} size={28}/>;
    let taskDescription = <span className={deploymentTaskStatusStyles.taskTimeText}>{DateFormatter.dateToShortFormat(relevantTime)}</span>;
    let link = links.deploymentPage.generateUrl({ spaceId, projectSlug, releaseVersion: deployment.ReleaseVersion, deploymentId: deployment.DeploymentId });
    if (liveStatus && (forceLiveStatusIcon || showLiveStatus(deployment.State, deployment.HasPendingInterruptions))) {
        taskStatusIcon = <LiveStatusIcon status={liveStatus.Status} size={28}/>;
        taskDescription = <LiveStatusDescription status={liveStatus.Status}/>;
        link = liveStatus.TenantId
            ? links.tenantedApplicationLiveStatusPage.generateUrl({ spaceId, projectSlug, environmentId: liveStatus.Id, tenantId: liveStatus.TenantId })
            : links.untenantedApplicationLiveStatusPage.generateUrl({ spaceId, projectSlug, environmentId: liveStatus.Id });
    }
    return (<InternalLink ref={containerRef} to={link} className={deploymentTaskStatusStyles.root}>
            <div className={cx(deploymentTaskStatusStyles.taskIcon, additionalTaskStyles(deployment))}>{taskStatusIcon}</div>
            {!isCollapsed && (<div className={deploymentTaskStatusStyles.task}>
                    <div className={deploymentTaskStatusStyles.releaseVersionContainer}>
                        <span className={releaseVersionTextStyles} title={deployment.ReleaseVersion}>
                            {deployment.ReleaseVersion}
                        </span>
                        {<TenantIcon tenantProgress={tenantProgress}/>}
                    </div>
                    {channelName && (<div>
                            <ChannelChip channelName={channelName} noMargin={true}/>
                        </div>)}
                    {taskDescription}
                </div>)}
            {isCollapsed && <div className={collapsedDeploymentTaskDetailStyles}>{tenantProgress !== "hidden" ? <StyledTenantsIcon /> : <SpacerIcon />}</div>}
        </InternalLink>);
};
const TenantIcon = ({ tenantProgress }: {
    tenantProgress: TenantDeploymentProgress | "tenanted" | "hidden";
}) => {
    if (tenantProgress === "tenanted") {
        return <StyledTenantsIcon />;
    }
    if (tenantProgress === "hidden") {
        return <SpacerIcon />;
    }
    return <Badge size={"xSmall"} variant={"inverseNeutral"} label={`${tenantProgress.CompletedDeployments}/${tenantProgress.TotalConnectedTenants}`}/>;
};
// We only want to show the live status icon when the deployment is in a standard, healthy state.
// Otherwise, we prefer to show the error state of the deployment.
function showLiveStatus(deploymentState: TaskState, hasPendingInterruptions: boolean) {
    if (hasPendingInterruptions) {
        return false;
    }
    return deploymentState === TaskState.Queued || deploymentState === TaskState.Executing || deploymentState === TaskState.Success;
}
function additionalTaskStyles(deployment: DetailedDashboardItemResource | ProjectsPageDeployment): string {
    switch (true) {
        case (deployment as DetailedDashboardItemResource).IsPrevious: // eslint-disable-line @typescript-eslint/consistent-type-assertions
            return deploymentTaskStatusStyles.isPrevious;
        case (deployment as DetailedDashboardItemResource).IsCurrent === false: // eslint-disable-line @typescript-eslint/consistent-type-assertions
            return deploymentTaskStatusStyles.isArchived;
        default:
            return "";
    }
}
const releaseVersionTextStyles = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "8.5rem", // Needed for ellipsis to work
    font: text.body.bold.medium,
    lineHeight: "1.25rem",
    color: themeTokens.color.text.primary,
    flex: 1,
});
const deploymentTaskStatusStyles = {
    root: css({
        display: "flex",
        flexDirection: "row",
        columnGap: space[8],
        padding: space[6],
        overflow: "hidden", // Fallback/safety-net in case dates or anything else overflows here.
        minHeight: "3.25rem",
        borderRadius: borderRadius.small,
        "&:hover": {
            cursor: "pointer",
            outline: `1px solid ${themeTokens.color.border.selected}`,
            backgroundColor: themeTokens.color.background.primary.hover,
            [`& .${releaseVersionTextStyles}`]: {
                color: themeTokens.color.text.link.default,
            },
        },
        ":focus-visible": {
            boxShadow: themeTokens.shadow.focused,
            outline: "none",
        },
    }),
    taskIcon: css({
        marginTop: space[4],
    }),
    task: css({
        display: "flex",
        flexDirection: "column",
        flex: 1,
    }),
    releaseVersionContainer: css({
        display: "flex",
        alignItems: "center",
    }),
    taskTimeText: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.xSmall,
        lineHeight: "1.25rem",
    }),
    isPrevious: css({
        opacity: 0.65,
    }),
    isArchived: css({
        opacity: 0.2,
    }),
};
const collapsedDeploymentTaskDetailStyles = css({
    display: "flex",
    justifyContent: "flex-end",
    flex: 1,
});
function StyledTenantsIcon() {
    return (<Tooltip content="Tenanted">
            <div className={tenantIconStyles}>
                <TenantsIcon size={24}/>
            </div>
        </Tooltip>);
}
function SpacerIcon() {
    return (<div className={cx(tenantIconStyles, visuallyHidden)}>
            <TenantsIcon size={24}/>
        </div>);
}
const tenantIconStyles = css({
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    color: themeTokens.color.icon.secondary,
    cursor: "pointer",
    display: "flex",
    width: "1.25rem",
    height: "1.25rem",
});
const visuallyHidden = css({
    visibility: "hidden",
});
